import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, NgZone, Inject } from '@angular/core';
import { NavController } from '@ionic/angular';
import { BehaviorSubject, Subject, take, map, distinctUntilChanged, Observable, combineLatest } from 'rxjs';

import { routes } from 'src/app/constants';

import { GlobalCreateService } from './global-create.service';

export interface CommandBarAction {
  id: string; // Unique identifier for the action
  name: string; // Display name for the action
  icon?: string; // Optional: for an icon
  keywords?: string; // Optional: for better searchability
  callback: () => void; // Function to execute when action is triggered
  section?: string; // Optional: to group actions visually in the command bar
  shortcut?: {
    key: string; // The main key, e.g., 'H', 'K', 'C' (case-insensitive for matching)
    metaKey?: boolean; // Cmd on Mac / Windows key on Win. For Ctrl on Win, use ctrlKey.
    ctrlKey?: boolean; // Ctrl key on all platforms
    altKey?: boolean; // Alt/Option key
    shiftKey?: boolean; // Shift key
  };
  requiredContext?: string; // New property for contextual availability
  // We could add more properties like shortcut, etc. later
}

@Injectable({
  providedIn: 'root',
})
export class CustomCommandBarService implements OnDestroy {
  private allRegisteredActions = new BehaviorSubject<CommandBarAction[]>([]); // Holds ALL registered actions
  private activeContexts = new BehaviorSubject<Set<string>>(new Set()); // Holds active contexts

  // actions$ will now be a derived observable that filters based on activeContexts
  actions$: Observable<CommandBarAction[]>;

  private isOpen = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpen.asObservable();

  private actionTriggered = new Subject<{ actionId: string; context?: any }>();
  actionTriggered$ = this.actionTriggered.asObservable();

  // --- Start: Properties merged from ConversationCommandService ---
  private closeConversationSource = new Subject<void>();
  closeConversation$ = this.closeConversationSource.asObservable();

  private reopenConversationSource = new Subject<void>();
  reopenConversation$ = this.reopenConversationSource.asObservable();

  private addTagsToConversationSource = new Subject<void>();
  addTagsToConversation$ = this.addTagsToConversationSource.asObservable();

  private reassignConversationSource = new Subject<void>();
  reassignConversation$ = this.reassignConversationSource.asObservable();

  private changePrioritySource = new Subject<void>();
  changePriority$ = this.changePrioritySource.asObservable();

  private openConversationInSlackSource = new Subject<void>();
  openConversationInSlack$ = this.openConversationInSlackSource.asObservable();

  private snoozeConversationSource = new Subject<void>();
  snoozeConversation$ = this.snoozeConversationSource.asObservable();

  private composeReplySource = new Subject<void>();
  composeReply$ = this.composeReplySource.asObservable();

  private changeStatusSource = new Subject<void>();
  changeStatus$ = this.changeStatusSource.asObservable();

  private addFollowerSource = new Subject<void>();
  addFollower$ = this.addFollowerSource.asObservable();

  private composeInternalNoteSource = new Subject<void>();
  composeInternalNote$ = this.composeInternalNoteSource.asObservable();

  private searchKnowledgeBaseSource = new Subject<void>();
  searchKnowledgeBase$ = this.searchKnowledgeBaseSource.asObservable();

  // --- End: Properties merged from ConversationCommandService ---

  private globalKeydownListener: (event: KeyboardEvent) => void;
  private currentRegisteredActionsSnapshot: CommandBarAction[] = []; // For shortcut listener

  constructor(
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
    private navController: NavController,
    private globalCreateService: GlobalCreateService,
  ) {
    // Derived actions$ observable
    const globalSection = 'Navigation';

    // Global actions
    this.registerAction({
      id: 'navigate-home',
      name: 'Go to Home',
      icon: 'home-outline',
      keywords: 'dashboard main page',
      section: globalSection,
      callback: () => this.navController.navigateRoot(`/${routes.DASHBOARD}/${routes.HOME}`),
    });

    this.registerAction({
      id: 'navigate-inbox-me',
      name: 'Go to Inbox Assigned to Me',
      icon: 'file-tray-outline',
      keywords: 'messages unread',
      section: globalSection,
      callback: () => this.navController.navigateRoot(`/${routes.DASHBOARD}/${routes.INBOX}/me`),
    });

    this.registerAction({
      id: 'navigate-inbox-all',
      name: 'Go to Inbox All Conversations',
      icon: 'file-tray-outline',
      keywords: 'messages all',
      section: globalSection,
      callback: () => this.navController.navigateRoot(`/${routes.DASHBOARD}/${routes.INBOX}/all`),
    });

    this.registerAction({
      id: 'open-profile',
      name: 'Go to Profile',
      icon: 'person-circle-outline',
      section: globalSection,
      callback: () => this.navController.navigateRoot(`/profile`),
    });

    this.registerAction({
      id: 'create-conversation',
      name: 'Create Conversation',
      icon: 'add-circle-outline',
      keywords: 'new conversation ticket issue start',
      section: globalSection,
      callback: () => this.globalCreateService.conversation(),
    });

    // this.registerAction({
    //   id: 'contact-support-custom',
    //   name: 'Contact Support',
    //   icon: 'help-buoy-outline',
    //   keywords: 'help chat support unthread',
    //   section: globalSection,
    //   callback: () => {
    //     (window as any).$unthread.inAppChat('toggle', 'show');
    //   },
    // });

    this.registerAction({
      id: 'contact-support-custom',
      name: 'Support and Documentation',
      icon: 'help-buoy-outline',
      keywords: 'help chat support unthread',
      section: globalSection,
      callback: () => {
        window.open('https://docs.unthread.io', '_blank');
      },
    });

    // Conversation specific actions
    this.registerAction({
      id: 'conversation-close',
      name: 'Close Conversation',
      icon: 'close-circle-outline',
      keywords: 'complete resolve ticket issue',
      section: 'Conversation Actions',
      shortcut: { key: 'e', metaKey: true, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerCloseConversation(),
    });

    this.registerAction({
      id: 'conversation-reopen',
      name: 'Re-open Conversation',
      icon: 'refresh-circle-outline',
      keywords: 'unresolve ticket issue reopen',
      section: 'Conversation Actions',
      shortcut: { key: 'o', metaKey: true, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerReopenConversation(),
    });

    this.registerAction({
      id: 'conversation-add-tags',
      name: 'Add Tag to Conversation',
      icon: 'pricetags-outline',
      keywords: 'label categorize ticket issue',
      section: 'Conversation Actions',
      shortcut: { key: 't', metaKey: false, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerAddTagsToConversation(),
    });

    this.registerAction({
      id: 'conversation-change-status',
      name: 'Change Conversation Status',
      icon: 'pulse-outline',
      keywords: 'status change ticket issue',
      section: 'Conversation Actions',
      shortcut: { key: 's', metaKey: false, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerChangeStatus(),
    });

    this.registerAction({
      id: 'conversation-reassign',
      name: 'Reassign Conversation',
      icon: 'person-add-outline',
      keywords: 'assign owner user ticket issue re-assign',
      section: 'Conversation Actions',
      shortcut: { key: 'a', metaKey: false, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerReassignConversation(),
    });

    this.registerAction({
      id: 'conversation-change-priority',
      name: 'Change Conversation Priority',
      icon: 'cellular-outline',
      keywords: 'importance level ticket issue',
      section: 'Conversation Actions',
      shortcut: { key: 'p', metaKey: false, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerChangePriority(),
    });

    this.registerAction({
      id: 'conversation-open-in-slack',
      name: 'Open Conversation in Slack',
      icon: 'logo-slack',
      keywords: 'view original message thread ticket',
      section: 'Conversation Actions',
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerOpenConversationInSlack(),
    });

    this.registerAction({
      id: 'conversation-snooze',
      name: 'Snooze Conversation',
      icon: 'alarm-outline',
      keywords: 'remind later hold ticket issue',
      section: 'Conversation Actions',
      shortcut: { key: 'z', metaKey: false, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerSnoozeConversation(),
    });

    this.registerAction({
      id: 'conversation-compose-reply',
      name: 'Compose Reply to Conversation',
      icon: 'pencil-outline',
      keywords: 'answer respond message ticket issue',
      section: 'Conversation Actions',
      shortcut: { key: 'r', metaKey: false, shiftKey: true },
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerComposeReply(),
    });

    this.registerAction({
      id: 'conversation-add-follower',
      name: 'Add Follower to Conversation',
      icon: 'notifications-circle-outline',
      keywords: 'follow notify watch subscriber ticket issue',
      section: 'Conversation Actions',
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerAddFollower(),
    });

    this.registerAction({
      id: 'conversation-search-knowledge-base',
      name: 'Search Knowledge Base',
      icon: 'library-outline',
      keywords: 'kb articles help documentation search ticket issue',
      section: 'Conversation Actions',
      requiredContext: 'conversationDetailsActive',
      callback: () => this.triggerSearchKnowledgeBase(),
    });

    this.actions$ = combineLatest([this.allRegisteredActions, this.activeContexts]).pipe(
      map(([actions, contexts]) => {
        return actions
          .filter((action) => {
            // MODIFIED: For 'Conversation Actions', only check the URL.
            if (action.section === 'Conversation Actions') {
              return this.document.location.pathname.includes('conversations');
            }
            // For other actions, the original context logic still applies.
            const contextIsMet = !action.requiredContext || contexts.has(action.requiredContext);
            return contextIsMet;
          })
          .sort((a, b) => {
            // Keep sorting logic
            if (a.section && b.section) {
              if (a.section !== b.section) return a.section.localeCompare(b.section);
            } else if (a.section) return -1;
            else if (b.section) return 1;
            return a.name.localeCompare(b.name);
          });
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)), // Only emit if filtered list changes
    );

    // Update snapshot for shortcut listener whenever allRegisteredActions changes
    this.allRegisteredActions.subscribe((actions) => {
      this.currentRegisteredActionsSnapshot = actions;
    });

    this.globalKeydownListener = (event: KeyboardEvent) => {
      this.ngZone.run(() => {
        const target = event.target as HTMLElement;
        const isInputFocused =
          target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable;

        let commandBarIsOpen = false;
        this.isOpen$.pipe(take(1)).subscribe((open) => (commandBarIsOpen = open));

        if (event.key === 'Escape') {
          if (commandBarIsOpen) {
            this.close();
            event.preventDefault();
            return;
          }
        }

        // Cmd/Ctrl + K to toggle command bar
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          // Allow toggling if input is not focused, OR if command bar is already open (to close it).
          if (!isInputFocused || commandBarIsOpen) {
            event.preventDefault();
            this.toggle();
            return;
          }
        }

        // If command bar is open, or an input is focused, don't process other global shortcuts.
        // This check ensures that Cmd/Ctrl+K (if input is focused and bar is closed) doesn't get blocked here.
        if (commandBarIsOpen || isInputFocused) {
          return;
        }

        // Process other registered shortcuts if command bar is NOT open AND no input is focused
        for (const action of this.currentRegisteredActionsSnapshot) {
          if (action.shortcut) {
            const s = action.shortcut; // s for shortcut object
            const e = event; // e for event object

            if (s.key.toLowerCase() !== e.key.toLowerCase()) {
              continue;
            }

            const eventMetaOrCtrl = e.metaKey || e.ctrlKey;

            let metaCheck = true;
            if (s.metaKey === true && !eventMetaOrCtrl) metaCheck = false;
            if (s.metaKey === false && eventMetaOrCtrl) metaCheck = false;
            // If s.metaKey is undefined, metaCheck remains true (don't care condition)

            let ctrlCheck = true;
            if (s.ctrlKey === true && !e.ctrlKey) ctrlCheck = false;
            if (s.ctrlKey === false && e.ctrlKey) ctrlCheck = false;

            let altCheck = true;
            if (s.altKey === true && !e.altKey) altCheck = false;
            if (s.altKey === false && e.altKey) altCheck = false;

            let shiftCheck = true;
            if (s.shiftKey === true && !e.shiftKey) shiftCheck = false;
            if (s.shiftKey === false && e.shiftKey) shiftCheck = false;

            if (metaCheck && ctrlCheck && altCheck && shiftCheck) {
              // Modifiers match. Now check context.
              let contextIsMet = false;
              const activeContextsNow = this.activeContexts.value;

              if (action.requiredContext) {
                contextIsMet = activeContextsNow.has(action.requiredContext);
              } else {
                // No specific context required, so it's considered met (e.g., truly global shortcuts)
                contextIsMet = true;
              }

              if (contextIsMet) {
                e.preventDefault();
                action.callback();
                this.actionTriggered.next({ actionId: action.id, context: action.requiredContext });
                return; // Shortcut executed, stop further processing for this event
              }
            }
          }
        }
      });
    };
    this.document.addEventListener('keydown', this.globalKeydownListener);
  }

  ngOnDestroy(): void {
    if (this.globalKeydownListener) {
      this.document.removeEventListener('keydown', this.globalKeydownListener);
    }
  }

  registerContext(context: string): void {
    const currentContexts = this.activeContexts.value;
    if (!currentContexts.has(context)) {
      const newContexts = new Set(currentContexts);
      newContexts.add(context);
      this.activeContexts.next(newContexts);
    }
  }

  unregisterContext(context: string): void {
    const currentContexts = this.activeContexts.value;
    if (currentContexts.has(context)) {
      const newContexts = new Set(currentContexts);
      newContexts.delete(context);
      this.activeContexts.next(newContexts);
    }
  }

  registerAction(action: CommandBarAction): void {
    const currentActions = this.allRegisteredActions.value;
    if (currentActions.find((a) => a.id === action.id)) {
      console.warn(`Action with id ${action.id} already registered.`);
      return;
    }
    this.allRegisteredActions.next([...currentActions, action]);
  }

  unregisterAction(actionId: string): void {
    const currentActions = this.allRegisteredActions.value;
    this.allRegisteredActions.next(currentActions.filter((a) => a.id !== actionId));
  }

  triggerAction(actionId: string, context?: any): void {
    // Find from allRegisteredActions as the UI (filtered actions$) might not include it if context changed rapidly
    const action = this.allRegisteredActions.value.find((a) => a.id === actionId);
    if (action && action.callback) {
      // Optional: double check context if really strict, but usually UI selection implies context was met
      // if (action.requiredContext && !this.activeContexts.value.has(action.requiredContext)) {
      //   console.warn(`Triggered action ${actionId} but its context ${action.requiredContext} is not active.`);
      //   this.close();
      //   return;
      // }
      action.callback();
      this.actionTriggered.next({ actionId, context });
      this.close();
    } else {
      console.warn(`Action with id ${actionId} not found or has no callback.`);
    }
  }

  open(): void {
    this.isOpen.next(true);
  }

  close(): void {
    this.isOpen.next(false);
  }

  toggle(): void {
    this.isOpen.next(!this.isOpen.value);
  }

  // --- Start: Trigger methods merged from ConversationCommandService ---
  triggerCloseConversation(): void {
    this.closeConversationSource.next();
  }

  triggerReopenConversation(): void {
    this.reopenConversationSource.next();
  }

  triggerAddTagsToConversation(): void {
    this.addTagsToConversationSource.next();
  }

  triggerReassignConversation(): void {
    this.reassignConversationSource.next();
  }

  triggerChangePriority(): void {
    this.changePrioritySource.next();
  }

  triggerOpenConversationInSlack(): void {
    this.openConversationInSlackSource.next();
  }

  triggerSnoozeConversation(): void {
    this.snoozeConversationSource.next();
  }

  triggerComposeReply(): void {
    this.composeReplySource.next();
  }

  triggerChangeStatus(): void {
    this.changeStatusSource.next();
  }

  triggerAddFollower(): void {
    this.addFollowerSource.next();
  }

  triggerComposeInternalNote(): void {
    this.composeInternalNoteSource.next();
  }

  triggerSearchKnowledgeBase(): void {
    this.searchKnowledgeBaseSource.next();
  }

  // --- End: Trigger methods merged from ConversationCommandService ---
}
